"use client";

import { create } from "zustand";
import { nanoid } from "nanoid";
import type { ComponentDefinition } from "./types";

/**
 * Saved custom components, `localStorage` only. The snapshot keeps the
 * definition's own `id`, which is what keeps a customized catalog part linked
 * to its original so "Reset to catalog" still works after the drop.
 */

const STORAGE_KEY = "twelve-volt:custom-library";

export type CustomLibraryEntry = {
  /** Stable library id, separate from the inline definition `id`. */
  libId: string;
  definition: ComponentDefinition;
  /** Unix ms. Drives sort order. */
  savedAt: number;
};

function isComponentDefinition(v: unknown): v is ComponentDefinition {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.id === "string" &&
    typeof o.name === "string" &&
    typeof o.category === "string" &&
    typeof o.width === "number" &&
    typeof o.height === "number" &&
    Array.isArray(o.terminals)
  );
}

function isEntry(v: unknown): v is CustomLibraryEntry {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.libId === "string" &&
    typeof o.savedAt === "number" &&
    isComponentDefinition(o.definition)
  );
}

function availableStorage(): Storage | null {
  try {
    if (typeof window !== "undefined") return window.localStorage;

    // Unit tests may provide a concrete global storage object. Avoid reading
    // Node's built-in getter here: without `--localstorage-file` it is only a
    // placeholder and emits a warning while the SSR bundle is evaluated.
    const descriptor = Object.getOwnPropertyDescriptor(
      globalThis,
      "localStorage",
    );
    return descriptor && "value" in descriptor
      ? (descriptor.value as Storage)
      : null;
  } catch {
    return null;
  }
}

/** Returns `[]` when missing, malformed, or outside a browser. */
export function loadLibrary(): CustomLibraryEntry[] {
  const storage = availableStorage();
  if (!storage || typeof storage.getItem !== "function") return [];
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isEntry);
  } catch (e) {
    console.warn("Failed to load custom library", e);
    return [];
  }
}

function persist(entries: CustomLibraryEntry[]) {
  const storage = availableStorage();
  if (!storage || typeof storage.setItem !== "function") return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch (e) {
    console.warn("Failed to save custom library", e);
  }
}

interface CustomLibraryState {
  entries: CustomLibraryEntry[];
  /** Snapshots `definition`; returns the generated `libId`. */
  save(definition: ComponentDefinition): string;
  remove(libId: string): void;
}

export const useCustomLibrary = create<CustomLibraryState>((set, get) => ({
  entries: loadLibrary(),
  save: (definition) => {
    // Deep clone: later edits to the source node must not reach the entry.
    const snapshot: ComponentDefinition = JSON.parse(JSON.stringify(definition));
    const entry: CustomLibraryEntry = {
      libId: nanoid(8),
      definition: snapshot,
      savedAt: Date.now(),
    };
    const next = [entry, ...get().entries];
    persist(next);
    set({ entries: next });
    return entry.libId;
  },
  remove: (libId) => {
    const next = get().entries.filter((e) => e.libId !== libId);
    if (next.length === get().entries.length) return;
    persist(next);
    set({ entries: next });
  },
}));
